<template>
  <main class="meta-speed-page">
    <header class="meta-speed-header">
      <div class="meta-speed-brand-wrap">
        <router-link class="meta-speed-logo" to="/home" aria-label="Medusa home">
          <img v-if="logoUrl" :src="logoUrl" alt="Medusa" />
          <span v-else class="meta-speed-logo-fallback">
            <img src="/images/medusa.png" alt="" />
            <strong>MEDUSA</strong>
          </span>
        </router-link>
      </div>

      <nav class="meta-speed-auth" aria-label="Account actions">
        <router-link class="meta-speed-login" to="/login">
          <LockClosedIcon aria-hidden="true" />
          <span>Login</span>
        </router-link>
        <router-link class="meta-speed-join" :to="joinRoute">Join Medusa</router-link>
      </nav>
    </header>

    <section class="meta-speed-hero">
      <a class="meta-speed-hero-link" :href="typeformUrl" aria-label="Apply for wholesale access"></a>
      <picture v-for="(slide, index) in heroSlides" :key="slide.desktop" class="meta-speed-hero-slide"
        :class="{ 'is-active': index === activeHeroSlide }" aria-hidden="true">
        <source media="(max-width: 767px)" :srcset="slide.mobile" />
        <img :src="slide.desktop" alt="" :loading="index === 0 ? 'eager' : 'lazy'"
          :fetchpriority="index === 0 ? 'high' : 'auto'" />
      </picture>
      <div class="meta-speed-hero-copy">
        <h1>Restock your entire store<br />before your coffee gets cold.</h1>
        <p>Orders shipped in 24hrs</p>
      </div>
    </section>

    <section class="meta-speed-primary-cta" aria-labelledby="primary-cta-title">
      <h2 id="primary-cta-title" class="sr-only">Apply for wholesale access</h2>
      <a class="meta-speed-cta meta-speed-cta-dark" :href="typeformUrl">
        Apply for wholesale access
      </a>
      <p>(Takes about 7 minutes.)</p>
    </section>

    <section class="meta-speed-testimonial" aria-label="Customer testimonial">
      <h2>Free inventory analysis based on millions of orders</h2>
      <div class="meta-speed-stars" aria-label="5 out of 5 stars">
        <SolidStarIcon v-for="star in 5" :key="star" aria-hidden="true" />
      </div>
      <blockquote>
        “The reps make sure we have the information we need to get the best
        products for our store. Partnering with Medusa has increased our
        profits, improved our in-stock rates and allowed us to deliver the best
        products at the best prices to our customers.”
      </blockquote>
      <p class="meta-speed-attribution">
        <strong>—AZ TROENKRASNOW</strong>
        <span>THE MUSHROOM | NEW ORLEANS, LA</span>
      </p>
    </section>

    <section class="meta-speed-benefits" aria-label="Why stores choose Medusa">
      <ul class="meta-speed-benefit-list">
        <li v-for="benefit in benefits" :key="benefit.title">
          <span class="meta-speed-benefit-icon" aria-hidden="true">
            <component :is="benefit.icon" />
          </span>
          <span>
            <strong>{{ benefit.title }}</strong>
            <small>{{ benefit.description }}</small>
          </span>
        </li>
      </ul>

      <div class="meta-speed-brands" aria-label="Popular brands available through Medusa">
        <img v-for="brand in brands" :key="brand.name" :src="brand.image" :alt="brand.name" />
      </div>
    </section>

    <section class="meta-speed-final-cta" aria-labelledby="final-cta-title">
      <a class="meta-speed-final-cta-link" :href="typeformUrl" aria-label="Apply for wholesale access"></a>
      <h2 id="final-cta-title">A few minutes now. A faster store all year.</h2>
      <p>Quick approval, guaranteed.</p>
      <a class="meta-speed-cta meta-speed-cta-light" :href="typeformUrl">
        Apply for wholesale access
      </a>
    </section>
  </main>
</template>

<script>
import axios from "axios";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import {
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  StarIcon,
  SupportIcon,
} from "@heroicons/vue/outline";
import { StarIcon as SolidStarIcon } from "@heroicons/vue/solid";

const TYPEFORM_BASE_URL = "https://medusa.typeform.com/to/gmXiN3ZZ";

export default {
  name: "MetaDirectSpeed",
  components: { LockClosedIcon, SolidStarIcon },
  setup() {
    const route = useRoute();
    const logoUrl = ref("");
    const heroSlides = [
      {
        desktop: "/images/route/route-hero-desktop-01-nwtn.jpg",
        mobile: "/images/route/route-hero-mobile-01-nwtn.jpg",
      },
      {
        desktop: "/images/route/route-hero-desktop-02-mja.jpg",
        mobile: "/images/route/route-hero-mobile-02-mja.jpg",
      },
      {
        desktop: "/images/route/route-hero-desktop-03-puffco.jpg",
        mobile: "/images/route/route-hero-mobile-03-puffco.jpg",
      },
      {
        desktop: "/images/route/route-hero-desktop-04-raw.jpg",
        mobile: "/images/route/route-hero-mobile-04-raw.jpg",
      },
    ];
    const activeHeroSlide = ref(0);
    let heroTimer;

    const stopHeroSlideshow = () => {
      if (heroTimer) {
        window.clearInterval(heroTimer);
        heroTimer = undefined;
      }
    };

    const startHeroSlideshow = () => {
      stopHeroSlideshow();

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }

      heroTimer = window.setInterval(() => {
        activeHeroSlide.value =
          (activeHeroSlide.value + 1) % heroSlides.length;
      }, 6000);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopHeroSlideshow();
      } else {
        startHeroSlideshow();
      }
    };

    onMounted(() => {
      startHeroSlideshow();
      document.addEventListener("visibilitychange", handleVisibilityChange);
    });

    onBeforeUnmount(() => {
      stopHeroSlideshow();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    });

    axios
      .get(`${process.env.BASE_URL_API}/logo/get`)
      .then((response) => {
        logoUrl.value = response.data?.url || "";
      })
      .catch(() => {
        logoUrl.value = "";
      });

    const typeformUrl = computed(() => {
      const params = new URLSearchParams();

      Object.entries(route.query).forEach(([key, value]) => {
        const values = Array.isArray(value) ? value : [value];

        values.forEach((queryValue) => {
          if (queryValue !== null && queryValue !== undefined) {
            params.append(key, queryValue);
          }
        });
      });

      const queryString = params.toString();

      console.log("Typeform URL:", queryString ? `${TYPEFORM_BASE_URL}?${queryString}` : TYPEFORM_BASE_URL);

      return queryString ? `${TYPEFORM_BASE_URL}?${queryString}` : TYPEFORM_BASE_URL;
    });

    const joinRoute = computed(() => ({
      path: "/join-us",
      query: route.query,
    }));

    const benefits = [
      {
        title: "Live inventory counts.",
        description: "If you can order it, it’s in stock. No surprise out-of-stock emails here.",
        icon: ChartBarIcon,
      },
      {
        title: "Data just for you.",
        description: "Stock what’s about to sell in your location with confidence.",
        icon: CurrencyDollarIcon,
      },
      {
        title: "Fast fulfillment.",
        description: "Ships in hours, not days. Orders placed before 2pm ship same-day.",
        icon: ClockIcon,
      },
      {
        title: "In-house warranty support.",
        description: "Anything arrives wrong, we make it right. File claims with ease.",
        icon: ShieldCheckIcon,
      },
      {
        title: "Sell-through support.",
        description: "Digital assets, in-store merchandising, and store locator listings that send foot traffic your way.",
        icon: SupportIcon,
      },
      {
        title: "Brands every customer knows.",
        description: "And new ones that stand out, too.",
        icon: StarIcon,
      },
    ];

    const brands = [
      { name: "BigFun", image: "/images/top-brands/bigfun.png" },
      { name: "NWTN Home", image: "/images/top-brands/nwtnhome.png" },
      { name: "Human Grade", image: "/images/top-brands/humangrade.png" },
      { name: "Softglass", image: "/images/top-brands/softglass.png" },
      { name: "MJ Arsenal", image: "/images/top-brands/mj-arsenal.png" },
      { name: "Cartisan", image: "/images/top-brands/cartisan.png" },
      { name: "PAX", image: "/images/top-brands/pax.png" },
      { name: "Puffco", image: "/images/top-brands/puffco.png" },
      { name: "RAW", image: "/images/top-brands/raw.png" },
      { name: "Blazy Susan", image: "/images/top-brands/blazysusan.png" },
      { name: "Lookah", image: "/images/top-brands/lookah.png" },
      { name: "GRAV", image: "/images/top-brands/grav.png" },
    ];

    return {
      activeHeroSlide,
      benefits,
      brands,
      heroSlides,
      joinRoute,
      logoUrl,
      typeformUrl,
    };
  },
};
</script>

<style lang="scss" scoped>
.meta-speed-page {
  min-height: 100vh;
  overflow-x: hidden;
  background: #fff;
  color: #111;
  font-family: "GothamPro", Arial, sans-serif;
}

.meta-speed-header {
  display: flex;
  height: 52px;
  align-items: center;
  justify-content: space-between;
  padding: 0 6.5vw;
  background: #fff;
}

.meta-speed-brand-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.meta-speed-logo {
  display: inline-flex;
  height: 23px;
  align-items: center;
}

.meta-speed-logo>img {
  display: block;
  width: auto;
  height: 22px;
}

.meta-speed-logo-fallback {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #333;
  font-size: 19px;
}

.meta-speed-logo-fallback img {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.meta-speed-auth {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 14px;
  font-weight: 500;
}

.meta-speed-login {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #111;
}

.meta-speed-login svg {
  width: 15px;
  height: 15px;
}

.meta-speed-join {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  padding: 0 27px;
  border: 2px solid #111;
  border-radius: 7px;
  font-weight: 600;
  color: #111;
}

.meta-speed-hero {
  position: relative;
  width: 100%;
  max-height: 750px;
  aspect-ratio: 1440 / 712;
  overflow: hidden;
  background: #555;
}

.meta-speed-hero-link {
  position: absolute;
  z-index: 4;
  inset: 0;
  cursor: pointer;
}

.meta-speed-hero-slide {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 1400ms ease-in-out;
}

.meta-speed-hero-slide.is-active {
  opacity: 1;
}

.meta-speed-hero-slide img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
}

.meta-speed-hero::before {
  position: absolute;
  z-index: 2;
  inset: 0;
  background: linear-gradient(to bottom,
      rgba(0, 0, 0, 0.3) 0%,
      rgba(0, 0, 0, 0.13) 28%,
      rgba(0, 0, 0, 0) 52%);
  content: "";
}

.meta-speed-hero::after {
  position: absolute;
  z-index: 2;
  inset: 0;
  background: linear-gradient(to bottom,
      rgba(0, 0, 0, 0.04) 0%,
      rgba(0, 0, 0, 0.04) 70%,
      rgba(255, 255, 255, 0.16) 82%,
      rgba(255, 255, 255, 0.68) 94%,
      rgba(255, 255, 255, 1) 100%);
  content: "";
}

.meta-speed-hero-copy {
  position: relative;
  z-index: 3;
  padding-top: 34px;
  color: #fff;
  text-align: center;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.24);
}

.meta-speed-hero h1 {
  margin: 0;
  font-size: clamp(30px, 3.1vw, 42px);
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1.1;
}

.meta-speed-hero p {
  margin: 10px 0 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
}

.meta-speed-primary-cta {
  display: flex;
  min-height: 92px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 13px 20px 12px;
  background: #fff;
}

.meta-speed-cta {
  display: inline-flex;
  width: min(370px, calc(100vw - 40px));
  min-height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.meta-speed-cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 7px 20px rgba(0, 0, 0, 0.14);
}

.meta-speed-cta:focus-visible,
.meta-speed-hero-link:focus-visible,
.meta-speed-final-cta-link:focus-visible,
.meta-speed-login:focus-visible,
.meta-speed-join:focus-visible {
  outline: 3px solid #c19b72;
  outline-offset: 3px;
}

.meta-speed-cta-dark {
  background: #000000;
  color: #fff;
}

.meta-speed-primary-cta p {
  margin: 0;
  color: #555;
  font-size: 12px;
}

.meta-speed-testimonial {
  display: flex;
  min-height: 317px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px 28px;
  background: #f0e3d7;
  text-align: center;
}

.meta-speed-testimonial h2 {
  margin: 0;
  font-size: clamp(22px, 2.1vw, 28px);
  font-weight: 700;
  letter-spacing: -0.35px;
  line-height: 1.15;
}

.meta-speed-stars {
  display: flex;
  gap: 4px;
  margin: 20px 0 12px;
  color: #c29a70;
}

.meta-speed-stars svg {
  width: 34px;
  height: 34px;
}

.meta-speed-testimonial blockquote {
  margin: 0;
  max-width: 700px;
  font-size: 22px;
  font-weight: 200;
  line-height: 1.18;
}

.meta-speed-testimonial blockquote strong {
  font-weight: 700;
}

.meta-speed-attribution {
  display: flex;
  flex-direction: column;
  margin: 15px 0 0;
  font-size: 11px;
  line-height: 1.15;
}

.meta-speed-attribution strong {
  font-size: 14px;
  font-weight: 700;
}

.meta-speed-attribution span {
  color: #555;
  letter-spacing: 0.3px;
}

.meta-speed-benefits {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px 42px;
}

.meta-speed-benefit-list {
  width: min(760px, 100%);
  overflow: hidden;
  margin: 0;
  padding: 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  list-style: none;
}

.meta-speed-benefit-list li {
  display: grid;
  min-height: 84px;
  grid-template-columns: 64px minmax(0, 1fr);
  align-items: center;
  gap: 18px;
  padding: 15px 28px;
}

.meta-speed-benefit-list li+li {
  border-top: 1px solid #e0e0e0;
}

.meta-speed-benefit-icon {
  display: inline-flex;
  width: 52px;
  height: 52px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #8c8c8c;
  color: #ffffff;
}

.meta-speed-benefit-icon svg {
  width: 26px;
  height: 26px;
  stroke-width: 1.75;
}

.meta-speed-benefit-list strong,
.meta-speed-benefit-list small {
  display: block;
}

.meta-speed-benefit-list strong {
  color: #111111;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
}

.meta-speed-benefit-list small {
  margin-top: 3px;
  color: #444444;
  font-size: 13.5px;
  font-weight: 400;
  line-height: 1.35;
}

.meta-speed-brands {
  display: grid;
  width: min(760px, 100%);
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: center;
  gap: 28px 66px;
  margin-top: 60px;
}

.meta-speed-brands img {
  width: 100%;
  height: 52px;
  object-fit: contain;
}

.meta-speed-final-cta {
  position: relative;
  display: flex;
  min-height: 232px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 42px 24px;
  background-image: url("/images/meta-direct-speed-bottom.png");
  background-position: center;
  background-size: cover;
  color: #fff;
  text-align: center;
}

.meta-speed-final-cta::before {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.04);
  content: "";
}

.meta-speed-final-cta> :not(.meta-speed-final-cta-link) {
  position: relative;
  z-index: 3;
}

.meta-speed-final-cta-link {
  position: absolute;
  z-index: 2;
  inset: 0;
  cursor: pointer;
}

.meta-speed-final-cta h2 {
  margin: 0;
  font-size: clamp(26px, 2.6vw, 36px);
  font-weight: 600;
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.meta-speed-final-cta p {
  margin: 12px 0 24px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.25px;
  text-transform: uppercase;
}

.meta-speed-final-cta .meta-speed-cta {
  min-height: 42px;
  font-size: 13px;
  font-weight: 700;
}

.meta-speed-cta-light {
  background: #ffffff;
  color: #111111;
}

@media (max-width: 767px) {
  .meta-speed-header {
    height: 58px;
    padding: 0 18px;
  }

  .meta-speed-auth {
    gap: 12px;
    font-size: 13px;
  }

  .meta-speed-join {
    min-height: 36px;
    padding: 0 14px;
  }

  .meta-speed-logo,
  .meta-speed-logo>img {
    height: 21px;
  }

  .meta-speed-hero {
    height: auto;
    max-height: none;
    aspect-ratio: 375 / 480;
  }

  .meta-speed-hero-slide img {
    object-position: center center;
  }

  .meta-speed-hero-copy {
    padding: 34px 18px 0;
  }

  .meta-speed-hero h1 {
    font-size: clamp(24px, 6.4vw, 29px);
    font-weight: 700;
    letter-spacing: -0.5px;
    line-height: 1.12;
  }

  .meta-speed-hero p {
    margin-top: 10px;
    font-size: 12px;
    letter-spacing: 1.5px;
  }

  .meta-speed-cta {
    width: min(340px, calc(100vw - 32px));
    min-height: 42px;
    font-size: 13.5px;
  }

  .meta-speed-testimonial blockquote {
    font-size: 18px;
    line-height: 1.25;
  }

  .meta-speed-testimonial {
    min-height: 0;
    padding: 32px 20px 30px;
  }

  .meta-speed-testimonial h2 {
    max-width: 330px;
    font-size: 22px;
    line-height: 1.18;
  }

  .meta-speed-stars {
    margin: 18px 0 12px;
  }

  .meta-speed-stars svg {
    width: 30px;
    height: 30px;
  }

  .meta-speed-benefits {
    padding: 38px 16px 48px;
  }

  .meta-speed-benefit-list li {
    min-height: 84px;
    grid-template-columns: 44px minmax(0, 1fr);
    gap: 14px;
    padding: 14px 16px;
  }

  .meta-speed-benefit-icon {
    width: 44px;
    height: 44px;
  }

  .meta-speed-benefit-icon svg {
    width: 22px;
    height: 22px;
  }

  .meta-speed-benefit-list strong {
    font-size: 13.5px;
  }

  .meta-speed-benefit-list small {
    font-size: 12px;
  }

  .meta-speed-brands {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px 34px;
    margin-top: 42px;
  }

  .meta-speed-brands img {
    height: 43px;
  }

  .meta-speed-final-cta {
    min-height: 242px;
    padding: 42px 20px;
  }

  .meta-speed-final-cta h2 {
    max-width: 320px;
    font-size: clamp(22px, 6vw, 26px);
    line-height: 1.22;
  }

  .meta-speed-final-cta p {
    margin: 12px 0 22px;
    font-size: 11px;
    letter-spacing: 1.15px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .meta-speed-hero-slide {
    transition: none;
  }
}
</style>
